import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { type PromotionStatus, RecordListItem } from './RecordListItem';
import {
  formatSocialHistoryValue,
  getSocialHistoryEffectiveDate,
  getSocialHistoryTitle,
  groupLatestByIdentifier,
} from './socialHistoryUtils';

export type { PromotionStatus } from './RecordListItem';

export type SocialHistoryListProps = {
  observations: WithId<Observation>[];
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

const formatEffectiveDate = (observation: Observation): string | undefined => {
  const effectiveDate = getSocialHistoryEffectiveDate(observation);
  if (!effectiveDate) return undefined;
  try {
    return format(parseISO(effectiveDate), DATE_FORMATS.DATE);
  } catch {
    return undefined;
  }
};

export const SocialHistoryList = ({
  observations,
  onPromotionClick,
  getPromotionStatus,
}: SocialHistoryListProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const grouped = groupLatestByIdentifier(observations);

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-2">
        {grouped.length > 0 ? (
          grouped.map((obs) => {
            const value = formatSocialHistoryValue(obs);
            const date = formatEffectiveDate(obs);
            const description = date ? `${value} · ${date}` : value;
            return (
              <RecordListItem
                key={obs.id}
                title={getSocialHistoryTitle(obs)}
                description={description}
                onPromotionClick={onPromotionClick ? (): void => onPromotionClick(obs.id) : undefined}
                promotionStatus={getPromotionStatus?.(obs.id)}
              />
            );
          })
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('noSocialHistoryFound')}</p>
        )}
      </div>
    </div>
  );
};
